<template>
  <Modal 
    :visible="visible" 
    title="投注详情"
    headerClass="bg-blue-50 border-b border-blue-100"
    @close="$emit('close')"
  >
    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="loader"></div>
    </div>
    
    <!-- 投注详情内容 -->
    <div v-else class="p-2">
      <!-- 基本信息 -->
      <div v-if="!editMode" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div>
          <p class="text-sm text-gray-500 mb-1">注单ID</p>
          <p class="text-sm font-medium">{{ betData.fundsn || '-' }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-1">用户名</p>
          <p class="text-sm font-medium">{{ betData.vendor_username || '-' }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-1">局号</p>
          <p class="text-sm font-medium">{{ betData.roundid || '-' }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-1">游戏供应商</p>
          <p class="text-sm font-medium">{{ betData.system || '-' }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-1">游戏名称</p>
          <p class="text-sm font-medium">{{ betData.game || '-' }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-1">游戏类型</p>
          <p class="text-sm font-medium">{{ getGameCategoryName(betData) || '-' }}</p>
        </div>
        <div v-if="betData.detail && betData.detail.name_zh">
          <p class="text-sm text-gray-500 mb-1">游戏中文名</p>
          <p class="text-sm font-medium">{{ betData.detail.name_zh || '-' }}</p>
        </div>
        <div v-if="betData.detail && betData.detail.name_en">
          <p class="text-sm text-gray-500 mb-1">游戏英文名</p>
          <p class="text-sm font-medium">{{ betData.detail.name_en || '-' }}</p>
        </div>
      </div>

      <!-- 财务信息 -->
      <div v-if="!editMode" class="bg-blue-50 p-4 rounded-lg mb-6">
        <h4 class="text-sm font-semibold text-blue-800 mb-3">财务信息</h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-500 mb-1">投注金额</p>
            <p class="text-sm font-medium">{{ formatMoney(betData.bet_amount, betData.currency) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">派彩金额</p>
            <p class="text-sm font-medium" :class="{'text-green-600': parseFloat(betData.payout) > 0}">
              {{ formatMoney(betData.payout, betData.currency) }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">有效投注</p>
            <p class="text-sm font-medium">{{ formatMoney(betData.validbet_amount, betData.currency) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">币种</p>
            <p class="text-sm font-medium text-gray-900">{{ betData.currency || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">用户币种</p>
            <p class="text-sm font-medium text-gray-900">{{ betData.user_currency || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">汇率</p>
            <p class="text-sm font-medium text-gray-900">{{ betData.ugcr || '-' }}</p>
          </div>
        </div>
      </div>

      <!-- 状态信息 -->
      <div v-if="!editMode" class="bg-gray-50 p-4 rounded-lg mb-6">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">状态信息</h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-500 mb-1">当前状态</p>
            <div class="flex items-center">
              <span :class="getStatusBadgeClass(betData.status)">
                {{ betData.status_text || getStatusText(betData.status) }}
              </span>
              <span v-if="betData.is_abnormal" class="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                异常
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">投注时间</p>
            <p class="text-sm font-medium">{{ betData.bet_time || formatDate(betData.bet_at) }}</p>
          </div>
          <div v-if="betData.is_abnormal">
            <p class="text-xs text-gray-500 mb-1">结算时间</p>
            <p class="text-sm font-medium">{{ betData.settle_time || formatDate(betData.payout_at) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">创建时间</p>
            <p class="text-sm font-medium">{{ formatDate(betData.created_at) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">更新时间</p>
            <p class="text-sm font-medium">{{ formatDate(betData.updated_at) }}</p>
          </div>
          <div v-if="betData.is_abnormal" class="col-span-1 md:col-span-3">
            <p class="text-xs text-gray-500 mb-1">异常标注</p>
            <div class="flex items-center">
              <span class="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 mr-2">异常</span>
              <p class="text-sm font-medium">{{ betData.abnormal_reason || betData.abnormal_reason_text || '未提供异常原因' }}</p>
            </div>
            <p v-if="betData.abnormal_description" class="text-sm text-gray-600 mt-1">{{ betData.abnormal_description }}</p>
          </div>
        </div>
      </div>

      <!-- 游戏相关信息 -->
      <div v-if="hasGameInfo && !editMode" class="border border-gray-200 rounded-lg mb-6">
        <h4 class="text-sm font-semibold bg-gray-50 p-3 border-b border-gray-200">游戏相关信息</h4>
        <div class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="betData.game_id">
              <p class="text-xs text-gray-500 mb-1">游戏ID</p>
              <p class="text-sm font-medium">{{ betData.game_id }}</p>
            </div>
            <div v-if="betData.game_code">
              <p class="text-xs text-gray-500 mb-1">游戏代码</p>
              <p class="text-sm font-medium">{{ betData.game_code }}</p>
            </div>
            <div v-if="betData.game_type">
              <p class="text-xs text-gray-500 mb-1">游戏类型ID</p>
              <p class="text-sm font-medium">{{ betData.game_type }}</p>
            </div>
            <div v-if="betData.sub_game_id">
              <p class="text-xs text-gray-500 mb-1">子游戏ID</p>
              <p class="text-sm font-medium">{{ betData.sub_game_id }}</p>
            </div>
            <div v-if="betData.match_ids && betData.match_ids.length">
              <p class="text-xs text-gray-500 mb-1">比赛ID</p>
              <p class="text-sm font-medium">{{ betData.match_ids.join(', ') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 异常详情信息 -->
      <div v-if="betData.is_abnormal" class="bg-red-50 p-4 rounded-lg mb-6">
        <h4 class="text-sm font-semibold text-red-800 mb-3">异常信息</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="text-xs text-gray-500 mb-1">异常原因</p>
            <div class="flex items-center">
              <span class="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 mr-2">异常</span>
              <p class="text-sm font-medium">{{ betData.abnormal_reason || '未提供异常原因' }}</p>
            </div>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">异常描述</p>
            <p class="text-sm font-medium">{{ betData.abnormal_description || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">标记时间</p>
            <p class="text-sm font-medium">{{ formatDate(betData.abnormal_created_at) || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-1">标记管理员</p>
            <p class="text-sm font-medium">{{ betData.abnormal_admin_name || '-' }}</p>
          </div>
        </div>
      </div>

      <!-- 编辑模式 -->
      <div v-if="editMode && betData.is_abnormal" class="border border-gray-200 rounded-lg p-6 bg-white mb-6">
        <h4 class="text-lg font-medium text-gray-700 mb-4">编辑注单信息</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div class="flex items-center mb-2">
              <span class="text-sm text-gray-500 mr-2">原值:</span>
              <span class="text-sm font-medium text-gray-700">{{ formatMoney(originalData.bet_amount, betData.currency) }}</span>
            </div>
            <FormInput
              label="投注金额"
              v-model="editForm.bet_amount"
              type="number"
              step="0.01"
              :suffix="betData.currency"
              prefix-icon="fas fa-coins"
              class="w-full"
            />
          </div>
          
          <div>
            <div class="flex items-center mb-2">
              <span class="text-sm text-gray-500 mr-2">原值:</span>
              <span class="text-sm font-medium text-gray-700">{{ formatMoney(originalData.payout, betData.currency) }}</span>
            </div>
            <FormInput
              label="派彩金额"
              v-model="editForm.payout"
              type="number"
              step="0.01"
              :suffix="betData.currency"
              prefix-icon="fas fa-money-bill-wave"
              class="w-full"
            />
          </div>
          
          <div>
            <div class="flex items-center mb-2">
              <span class="text-sm text-gray-500 mr-2">原值:</span>
              <span class="text-sm font-medium text-gray-700">{{ formatMoney(originalData.validbet_amount, betData.currency) }}</span>
            </div>
            <FormInput
              label="有效投注"
              v-model="editForm.validbet_amount"
              type="number"
              step="0.01"
              :suffix="betData.currency"
              prefix-icon="fas fa-check-circle"
              class="w-full"
            />
          </div>
          
          <div>
            <div class="flex items-center mb-2">
              <span class="text-sm text-gray-500 mr-2">原值:</span>
              <span class="text-sm font-medium text-gray-700">{{ getStatusText(originalData.status) }}</span>
            </div>
            <FormSelect
              label="状态"
              v-model="editForm.status"
              prefix-icon="fas fa-toggle-on"
              class="w-full"
              :options="[{ value: '0', text: '未结算' }, { value: '1', text: '已结算' }, { value: '2', text: '已取消' }]"
            >
            </FormSelect>
          </div>
          
          <div>
            <FormInput
              label="余额调整"
              v-model="editForm.balance_adjustment"
              type="number"
              step="0.01"
              :suffix="betData.currency"
              prefix-icon="fas fa-hand-holding-usd"
              placeholder="正数增加余额，负数减少余额"
              class="w-full"
            />
            <p class="mt-1 text-xs text-gray-500">正数增加用户余额，负数减少用户余额</p>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div v-if="!editMode">
        <button @click="$emit('close')" class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition">关闭</button>
        <button 
          @click="toggleEditMode" 
          v-if="betData.is_abnormal"
          class="ml-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          编辑注单
        </button>
        <button 
          v-if="betData.is_abnormal && !betData.resolved" 
          @click="markAsResolved"
          :disabled="resolvingAbnormal"
          class="ml-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
          :class="{ 'opacity-75 cursor-not-allowed': resolvingAbnormal }"
        >
          <span v-if="resolvingAbnormal">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline-block text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            处理中...
          </span>
          <span v-else>标记为已解决</span>
        </button>
      </div>
      <div v-else>
        <button 
          @click="saveBetChanges" 
          :disabled="saving"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          :class="{ 'opacity-75 cursor-not-allowed': saving }"
        >
          <span v-if="saving">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline-block text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            保存中...
          </span>
          <span v-else>保存</span>
        </button>
        <button 
          @click="cancelEdit" 
          :disabled="saving"
          class="ml-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          :class="{ 'opacity-75 cursor-not-allowed': saving }"
        >
          取消
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Modal from '@/components/common/Modal.vue';
import FormInput from '@/components/common/FormInput.vue';
import FormSelect from '@/components/common/FormSelect.vue';
import AdminApiClient from '@/utils/admin-api-client';
const apiClient = new AdminApiClient();
const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  betData: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'abnormal-updated', 'mark-resolved', 'bet-updated']);

// 状态变量
const loading = ref(false);
const editMode = ref(false);
const saving = ref(false);
const resolvingAbnormal = ref(false);
const originalData = ref({});
const editForm = ref({
  bet_amount: '',
  payout: '',
  validbet_amount: '',
  status: '0',
  balance_adjustment: '',
  update_balance: true
});

// 检查是否有游戏相关信息
const hasGameInfo = computed(() => {
  return props.betData && (
    props.betData.game_id || 
    props.betData.game_code || 
    props.betData.game_type || 
    props.betData.sub_game_id || 
    (props.betData.match_ids && props.betData.match_ids.length)
  );
});

// 初始化加载
watch(() => props.visible, (newVal) => {
  if (newVal) {
    // 简单延迟，模拟加载过程
    loading.value = true;
    setTimeout(() => {
      loading.value = false;
    }, 300);
  }
});

// 格式化金额
function formatMoney(amount, currency = '') {
  if (amount === undefined || amount === null) return '-';
  
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '-';
  
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  });
  
  return `${formatter.format(numAmount)} ${currency}`.trim();
}

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return '-';
  
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  
  return date.toLocaleString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

// 获取状态文本
function getStatusText(status) {
  const statusMap = {
    '0': '未结算',
    '1': '已结算',
    '2': '已取消'
  };
  
  return statusMap[status] || status;
}

// 获取状态徽章类名
function getStatusBadgeClass(status) {
  const classMap = {
    '0': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800',
    '1': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800',
    '2': 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800'
  };
  
  return classMap[status] || 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800';
}

// 获取游戏类别名称
function getGameCategoryName(item) {
  const categoryMap = {
    'sport': '体育',
    'casino': '真人',
    'slot': '老虎机',
    'lottery': '彩票',
    'fish': '捕鱼',
    'chess': '棋牌',
    'arcade': '街机',
    'table': '桌牌'
  };
  
  return categoryMap[item.game_category] || item.game_category_name || item.game_category || '-';
}

// 切换编辑模式
function toggleEditMode() {
  editMode.value = true;
  
  // 保存原始数据
  originalData.value = {
    bet_amount: props.betData.bet_amount,
    payout: props.betData.payout,
    validbet_amount: props.betData.validbet_amount,
    status: props.betData.status
  };
  
  // 设置编辑表单初始值
  editForm.value = {
    bet_amount: props.betData.bet_amount,
    payout: props.betData.payout,
    validbet_amount: props.betData.validbet_amount,
    status: props.betData.status || '0',
    balance_adjustment: '',
    update_balance: '0'
  };
}

// 取消编辑
function cancelEdit() {
  editMode.value = false;
}

// 保存注单编辑
async function saveBetChanges() {
  saving.value = true;
  
  try {
    // 构建请求数据
    const data = {
      bet_id: props.betData.id,
      fundsn: props.betData.fundsn,
      system: props.betData.system,
      ...editForm.value
    };
    
    // 调用API保存修改
    const response = await apiClient.updateBet(data);
    
    if (response && response.success) {
      // 更新本地数据
      Object.assign(props.betData, {
        bet_amount: editForm.value.bet_amount,
        payout: editForm.value.payout,
        validbet_amount: editForm.value.validbet_amount,
        status: editForm.value.status
      });
      
      // 退出编辑模式
      editMode.value = false;
      
      // 触发成功事件
      emit('bet-updated', data);
      
      alert(response?.message);
    } else {
      alert('保存失败: ' + (response?.message || '未知错误'));
    }
  } catch (error) {
    console.error('保存注单信息失败:', error);
    alert('保存注单信息失败: ' + error.message);
  } finally {
    saving.value = false;
  }
}

// 标记异常为已解决
async function markAsResolved() {
  if (!props.betData.abnormal_id) {
    console.error('异常ID不存在，无法标记为已解决');
    alert('无法标记为已解决：缺少异常ID');
    return;
  }

  if (confirm('确定将此异常注单标记为已解决吗？')) {
    try {
      // 显示正在处理
      resolvingAbnormal.value = true;
      
      let params = {
        abnormal_id: props.betData.abnormal_id,
        resolution_notes: '通过投注详情页面手动标记为已解决'
      }
      
      // 调用解决异常注单的API
      const response = await apiClient.resolveAbnormalBet(params);
      
      // 处理响应
      if (response && response.success) {
        // 显示成功消息
        alert('异常已成功标记为已解决');
        await emit('mark-resolved', props.betData.abnormal_id);
        // 关闭模态框
        emit('close');
      } else {
        const errorMessage = response?.message || '操作失败';
        console.error('标记为已解决失败:', errorMessage);
        alert('操作失败: ' + errorMessage);
      }
    } catch (error) {
      console.error('标记异常为已解决失败:', error);
      alert('操作失败: ' + error.message);
      
      // 抛出错误以便调用方捕获
      throw error;
    } finally {
      resolvingAbnormal.value = false;
    }
  }
}
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 